/**
 * The Layout component is the top-level component of the dashboard application.
 *
 * It renders the Header, Nav, and SubMenu components, and renders its children
 * as the main content of the page.
 */

import React, { useEffect, useState } from "react";
import { Header } from "@src/common/components";
import { Nav } from "@src/common/components";
import { Sidebar } from "@src/common/components";
import { useRouter } from "next/router";
import Head from "next/head";
import { SUBMENU_ITEM } from "./types";
import { NAV_ITEMS, SUBMENU_ITEMS } from "./utils/navigationConstants.ts";
import { useCourieStore } from "@src/common/lib/store";
import { LayoutToast } from "./LayoutToast";
import { NavMenuContent } from "./NavMenuContent";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useLayout } from "./contexts/LayoutContext";
import { useAuth } from "@src/common/hooks/useAuth";

type Props = {
  children: React.ReactNode;
};

const initailCurrentPageState = SUBMENU_ITEMS[0];

export default function Layout({ children }: Props) {
  return <LoggedInLayout>{children}</LoggedInLayout>;
}

function LoggedInLayout({ children }: Props) {
  const router = useRouter();
  const { isRoleType, hasPermission } = useAuth();
  const { badgeCounts } = useLayout();
  const { widgets, toast } = useCourieStore();
  const [currentPage, setCurrentPage] = useState<SUBMENU_ITEM | undefined>(
    initailCurrentPageState
  );
  const [rightPanelVisible, setRightPanelVisible] = useState<boolean>(true);

  useEffect(() => {
    const pathname = router.pathname;
    if (pathname.includes("routingjob") || pathname.startsWith("/routes/")) {
      setCurrentPage(SUBMENU_ITEMS[0]);
      return;
    }
    const currentPage = SUBMENU_ITEMS.find((menuItem: SUBMENU_ITEM) => {
      if (pathname.includes(menuItem.page)) {
        return menuItem;
      }
    });
    setCurrentPage(currentPage);
  }, [router.pathname]);

  useEffect(() => {
    const isWidgetSelected = widgets.some((widget) => widget.selected);
    if (isWidgetSelected) {
      setRightPanelVisible(true);
    } else {
      setRightPanelVisible(false);
    }
  }, [widgets]);

  const getPopoverContent = (navItem: any) => {
    const submenuItem = SUBMENU_ITEMS.find(
      (menuItem: SUBMENU_ITEM) => menuItem.page === navItem.label.toLowerCase()
    );

    if (!submenuItem?.items) return null;

    const visibleItems = submenuItem.items.filter(
      (item) =>
        isRoleType(item.minimumUserRoleType) &&
        (!item.requiredPermission || hasPermission(item.requiredPermission))
    );

    return (
      <NavMenuContent items={visibleItems} activeHref={router?.pathname} />
    );
  };

  return (
    <>
      <Head>
        <title>Courie</title>
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <div
        className="dashboard-app-layout bg-slate-50 dark:bg-gray-900"
        style={{
          gridTemplateColumns: rightPanelVisible
            ? "70px auto 350px"
            : widgets.length > 0
            ? "70px auto 65px"
            : "70px auto 5px",
        }}
      >
        <Header className="app-header dark:text-gray-100">
          <Nav.Group>
            {NAV_ITEMS
              .filter(
                (item) =>
                  (!item.minimumUserRoleType ||
                    isRoleType(item.minimumUserRoleType)) &&
                  (!item.requiredPermission ||
                    hasPermission(item.requiredPermission))
              )
              .map((item: any, i: number) => (
              <Nav.Item
                key={i}
                {...item}
                active={
                  router?.pathname.includes(item.label.toLowerCase()) ||
                  (item.label === "Deliveries" &&
                    router?.pathname.startsWith("/routes/"))
                }
                badgeCount={
                  item.label === "Office" ? badgeCounts.customers || 0 : 0
                }
                popoverContent={getPopoverContent(item)}
              />
            ))}
          </Nav.Group>
        </Header>
        <Sidebar.Group className="app-sidebar dark:text-gray-100">
          <div className="flex-1 overflow-y-auto">
            {currentPage?.items &&
              currentPage.items
                .filter(
                  (x) =>
                    isRoleType(x.minimumUserRoleType) &&
                    (!x.requiredPermission ||
                      hasPermission(x.requiredPermission))
                )
                .map((item: any) => {
                  if (item.label === "Create Order") {
                    return null;
                  }
                  const label =
                    typeof item.label === "string" ? item.label : "";
                  const dataCy = label
                    ? `sidebar-${label.toLowerCase().replace(/\s+/g, "-")}`
                    : undefined;
                  return (
                    <div key={item.label} className="mb-3 ml-1">
                      <Sidebar.Item
                        {...item}
                        data-cy={dataCy}
                        active={router?.pathname === item.href}
                        badgeContent={
                          item.label === "Customers"
                            ? badgeCounts.customers || 0
                            : 0
                        }
                      />
                    </div>
                  );
                })}
          </div>
          <div className="flex-shrink-0 mb-4">
            <div className="mb-3">
              <Sidebar.Item
                label="Create"
                tooltipContent="Create New Order"
                href="/orders/create"
                active={router?.pathname === "/orders/create"}
                data-cy="sidebar-create-order"
                icon={
                  <PlusIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                }
              />
            </div>
          </div>
        </Sidebar.Group>
        <section className="app-content bg-slate-50 dark:bg-gray-900 dark:text-gray-100">
          {children}
        </section>
      </div>
      {toast && <LayoutToast toast={toast} />}
    </>
  );
}
